import { BASE_URL } from "../../../../shared/utils/variables";
import { getAuthHeaders } from "../../../../shared/services/authentication";

const baseURL = BASE_URL;

// Interface para os dados de pagamento mensal - ATUALIZADA
export interface BeneficiaryPayment {
    id: string;
    beneficiary: {
        id: string;
        name: string;
        type: string;
    };
    project: {
        id: string;
        name: string;
        financierId?: string;
        principalInvestigatorId?: string;
    };
    monthly_cost: number;
    monthly_cost_display: number;
    description?: string;
    is_paid_this_month: boolean;
    last_payment_date?: string | null;
    payment_status: string;
    created_at: string;
    project_end_date?: string | null;
    companyId?: string;
    companyName?: string;
}

export interface MonthlyPaymentData {
    year: number;
    month: number;
    month_name: string;
    totals: {
        total_beneficiaries: number;
        total_paid: number;
        total_pending: number;
        total_monthly_cost: number;
        total_monthly_cost_display: number;
        unique_projects: number;
        unique_companies: number;
    };
    beneficiaries: BeneficiaryPayment[];
    beneficiariesByCompany: {
        [companyId: string]: BeneficiaryPayment[];
    };
    companiesMap: {
        [companyId: string]: string;
    };
}

// Buscar beneficiários do mês (pagamentos mensais)
export const fetchMonthlyPaymentsService = async (year?: number | null, month?: number | null, companyId?: string | null): Promise<MonthlyPaymentData> => {
    try {
        // Construir query params com validação
        const currentDate = new Date();
        const queryYear = year ?? currentDate.getFullYear();
        const queryMonth = month ?? (currentDate.getMonth() + 1);
        
        let url = `${baseURL}/beneficiary-project/monthly?year=${queryYear}&month=${queryMonth}`;
        
        // Adicionar filtro de empresa se fornecido
        if (companyId && companyId !== 'all' && companyId !== '') {
            url += `&companyId=${companyId}`;
        }

        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(url, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar pagamentos mensais:', error);
        throw error;
    }
};

// Buscar relatório de custos por projeto
export const fetchProjectCostReportService = async (year?: number | null, month?: number | null, companyId?: string | null) => {
    try {
        const currentDate = new Date();
        const queryYear = year ?? currentDate.getFullYear();
        const queryMonth = month ?? (currentDate.getMonth() + 1);
        
        let url = `${baseURL}/beneficiary-project/cost-report?year=${queryYear}&month=${queryMonth}`;
        
        if (companyId && companyId !== 'all' && companyId !== '') {
            url += `&companyId=${companyId}`;
        }

        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(url, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar relatório de custos:', error);
        throw error;
    }
};

// Adicionar beneficiário a um projeto - ATUALIZADO
export const addBeneficiaryToProjectService = async (data: {
    beneficiaryId: string;
    projectId: string;
    monthly_cost: number;
    description?: string;
}) => {
    try {
        // Processar dados monetários
        const processedData = {
            ...data,
            monthly_cost: Math.round(data.monthly_cost * 100)
        };

        const options = {
            method: 'POST',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify(processedData)
        };

        const response = await fetch(`${baseURL}/beneficiary-project`, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao adicionar beneficiário ao projeto:', error);
        throw error;
    }
};

// Listar todos os vínculos beneficiário-projeto
export const fetchBeneficiaryProjectsService = async (search?: string, onlyActive?: boolean) => {
    try {
        let url = `${baseURL}/beneficiary-project`;
        const params = new URLSearchParams();
        
        if (search) {
            params.append('search', search);
        }
        
        if (onlyActive !== undefined) {
            params.append('only_active', String(onlyActive));
        }
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(url, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar vínculos beneficiário-projeto:', error);
        throw error;
    }
};

// Buscar vínculo por ID
export const fetchBeneficiaryProjectByIdService = async (id: string) => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Vínculo não encontrado');
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar vínculo:', error);
        throw error;
    }
};

// Atualizar vínculo beneficiário-projeto - ATUALIZADO
export const updateBeneficiaryProjectService = async (id: string, data: {
    monthly_cost?: number;
    description?: string;
    is_active?: boolean;
}) => {
    try {
        // Processar dados monetários se fornecidos
        const processedData = {
            ...data,
            ...(data.monthly_cost && { monthly_cost: Math.round(data.monthly_cost * 100) })
        };

        const options = {
            method: 'PUT',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify(processedData)
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao atualizar vínculo:', error);
        throw error;
    }
};

// Remover vínculo (soft delete)
export const deleteBeneficiaryProjectService = async (id: string) => {
    try {
        const options = {
            method: 'DELETE',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Vínculo não encontrado');
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao remover vínculo:', error);
        throw error;
    }
};

// Buscar projetos de um beneficiário específico
export const fetchBeneficiaryProjectsListService = async (beneficiaryId: string) => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary/${beneficiaryId}/projects`, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar projetos do beneficiário:', error);
        throw error;
    }
};

// Buscar beneficiários de um projeto específico
export const fetchProjectBeneficiariesService = async (projectId: string) => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/project/${projectId}/beneficiaries`, options);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('Erro ao buscar beneficiários do projeto:', error);
        throw error;
    }
};

// Função auxiliar para extrair array de resposta (mesma do segundo arquivo)
const extractArrayFromResponse = (data: any): any[] => {
    if (Array.isArray(data)) {
        return data;
    }
    
    if (!data) {
        return [];
    }
    
    if (typeof data === 'object') {
        const possibleArrayKeys = [
            'data', 'items', 'results', 'content', 'list', 
            'beneficiaryProjects', 'projects', 'beneficiaries'
        ];
        
        for (const key of possibleArrayKeys) {
            if (data[key] && Array.isArray(data[key])) {
                return data[key];
            }
        }
        
        for (const key in data) {
            if (Array.isArray(data[key])) {
                return data[key];
            }
        }
        
        return [];
    }
    
    return [];
};

// Buscar beneficiários (função auxiliar)
export const fetchBeneficiariesService = async (): Promise<any[]> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiaries`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ao buscar beneficiários: ${response.status}`);
        }
        
        const data = await response.json();
        const arrayData = extractArrayFromResponse(data);
        
        return arrayData;
    } catch (error) {
        console.error('Erro ao buscar beneficiários:', error);
        return [];
    }
};

// Buscar projetos (função auxiliar)
export const fetchProjectsService = async (): Promise<any[]> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/projects`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ao buscar projetos: ${response.status}`);
        }
        
        const data = await response.json();
        const arrayData = extractArrayFromResponse(data);
        
        return arrayData;
    } catch (error) {
        console.error('Erro ao buscar projetos:', error);
        return [];
    }
};